import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';
import { pricingPlans } from '../data/mock';

export default function RequestReviewPage() {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || '';
  const plan = pricingPlans.find((p) => p.id === planId);

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    // Simulate submission
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1000);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-28 pb-20 px-6 lg:px-8">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mb-4">
              Request Submitted
            </h1>
            <p className="text-stone-500 leading-relaxed mb-3">
              Thank you for your review request. We'll send instructions to{' '}
              <span className="font-semibold text-stone-700">{email}</span>{' '}
              with details on how to share your code and complete payment.
            </p>
            {plan && (
              <p className="text-sm text-stone-400 mb-8">
                Plan selected:{' '}
                <span className="font-semibold text-stone-600">
                  {plan.name} {plan.period}
                </span>{' '}
                — {plan.price} {plan.currency}
              </p>
            )}
            <div className="space-y-3">
              <div className="bg-stone-50 rounded-xl p-5 text-left text-sm text-stone-600 space-y-2 border border-stone-200">
                <p className="font-semibold text-stone-800">What happens next:</p>
                <ol className="list-decimal list-inside space-y-1.5 text-stone-500">
                  <li>You'll receive an email with a payment link</li>
                  <li>After payment, you'll get instructions to share your code</li>
                  <li>Our expert will begin the review once code is received</li>
                  <li>You'll receive your report at the email provided</li>
                </ol>
              </div>
              <Link to="/">
                <Button
                  variant="outline"
                  className="border-stone-300 text-stone-700 mt-4"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-28 pb-20 px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <Link
            to="/#pricing"
            className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors duration-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to plans
          </Link>

          <h1 className="text-3xl font-bold text-stone-900 mb-2">
            Request a Code Review
          </h1>
          <p className="text-stone-500 mb-8">
            Fill in your details and we'll get back to you with next steps.
          </p>

          {/* Selected plan display */}
          {plan ? (
            <div className="mb-8 p-5 rounded-xl bg-amber-50/60 border border-amber-200/60">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1">
                    Selected Plan
                  </p>
                  <p className="text-lg font-bold text-stone-900">
                    {plan.name}{' '}
                    <span className="text-sm font-mono text-stone-500">
                      {plan.period}
                    </span>
                  </p>
                  <p className="text-sm text-stone-500">
                    Up to {plan.lineLimit} lines of Python code
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-extrabold text-stone-900">
                    {plan.price}
                  </p>
                  <p className="text-sm text-stone-500">{plan.currency}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8 p-5 rounded-xl bg-stone-50 border border-stone-200">
              <p className="text-sm text-stone-500">
                No plan selected.{' '}
                <Link
                  to="/#pricing"
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Choose a plan
                </Link>{' '}
                or fill the form below for a general inquiry.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Hidden plan field */}
            <input type="hidden" name="plan" value={planId} />

            {/* Plan display (read-only) */}
            <div>
              <Label className="text-sm font-medium text-stone-700 mb-2 block">
                Plan
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  value={plan ? `${plan.name} ${plan.period}` : 'No plan selected'}
                  readOnly
                  className="bg-stone-50 text-stone-600 border-stone-200 cursor-not-allowed"
                />
                {plan && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 whitespace-nowrap">
                    {plan.price} {plan.currency}
                  </Badge>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <Label
                htmlFor="email"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Email Address <span className="text-rose-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20"
              />
            </div>

            {/* Message */}
            <div>
              <Label
                htmlFor="message"
                className="text-sm font-medium text-stone-700 mb-2 block"
              >
                Message{' '}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="message"
                placeholder="Tell us about your code, goals, or any specific concerns..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="border-stone-200 focus:border-amber-400 focus:ring-amber-400/20 resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !email}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white text-base font-semibold shadow-md shadow-amber-500/20 transition-[background-color,box-shadow] duration-200 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Submit Review Request
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
